import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Switch,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Save as SaveIcon, 
  Cancel as CancelIcon, 
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import api from '../api/api';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import { AppIconButton } from './ui';
import { CURRENCY } from '../config/currency';

interface PortionTemplate {
  id: number;
  name: string;
  templateType: string;
  options: Array<{ name: string; order: number }>;
}

interface Portion {
  id?: number;
  portionName: string;
  priceModifier: number;
  isDefault: boolean;
  displayOrder: number;
  displayPrice?: string;
  effectivePrice?: number;
}

interface MenuItemPortionsManagerProps {
  menuItemId: number;
  menuItemName: string;
  menuItemPrice: number;
  open: boolean;
  onClose: () => void;
  onPortionsUpdated: () => void;
}

// Utility function to validate base price
const validateBasePrice = (value: number): { valid: boolean; error?: string } => {
  if (value <= 0) {
    return { valid: false, error: `Base price must be greater than ${CURRENCY.symbol}0` };
  }
  if (value > 100000) {
    return { valid: false, error: 'Base price seems too high. Please verify.' };
  }
  return { valid: true };
};

const MenuItemPortionsManager: React.FC<MenuItemPortionsManagerProps> = ({
  menuItemId,
  menuItemName,
  menuItemPrice,
  open,
  onClose,
  onPortionsUpdated,
}) => {
  const [portions, setPortions] = useState<Portion[]>([]);
  const [templates, setTemplates] = useState<PortionTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | ''>('');
  const [editingPortion, setEditingPortion] = useState<Portion | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [localItemPrice, setLocalItemPrice] = useState(menuItemPrice);
  const [localPortions, setLocalPortions] = useState<Portion[]>([]);
  
  // Dialog state for simple default selection
  const [selectNewDefaultDialog, setSelectNewDefaultDialog] = useState<{
    open: boolean;
    currentDefaultPortion: Portion | null;
  }>({ open: false, currentDefaultPortion: null });

  useEffect(() => {
    if (open) {
      setInitializing(true);
      setLocalItemPrice(menuItemPrice);
      // Reset editing state when dialog opens
      setEditingPortion(null);
      setEditingIndex(null);
      setIsAddingNew(false);
      Promise.all([fetchPortions(), fetchTemplates()])
        .finally(() => setInitializing(false));
    }
  }, [open, menuItemId, menuItemPrice]);
  
  // Sync localPortions with portions
  useEffect(() => {
    setLocalPortions(portions);
  }, [portions]);

  const fetchPortions = async () => {
    try {
      const response = await api.get(`/api/menu-items/${menuItemId}/portions`);
      // Filter out any null or invalid portions
      let validPortions: Portion[] = (response.data || []).filter((p: any) => p && p.portionName);
      // Normalize: ensure exactly one default; if multiple, keep the first
      const firstDefaultIndex = validPortions.findIndex(p => p.isDefault);
      if (firstDefaultIndex !== -1) {
        validPortions = validPortions.map((p, idx) => ({
          ...p,
          isDefault: idx === firstDefaultIndex,
          priceModifier: idx === firstDefaultIndex ? 0 : (p.priceModifier || 0),
        }));
      } else if (validPortions.length > 0) {
        validPortions = validPortions.map((p, idx) => ({
          ...p,
          isDefault: idx === 0,
          priceModifier: idx === 0 ? 0 : (p.priceModifier || 0),
        }));
      }
      setPortions(validPortions);
    } catch (error) {
      console.error('Error fetching portions:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/menu-items/portion-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const newPortions = template.options.map((option, index) => ({
        portionName: option.name,
        priceModifier: 0,
        isDefault: index === 0,
        displayOrder: option.order,
      }));
      setPortions(newPortions);
    }
  };

  const handleAddPortion = () => {
    const newPortion: Portion = {
      portionName: '',
      priceModifier: 0,
      isDefault: portions.length === 0,
      displayOrder: portions.length + 1,
    };
    setEditingPortion(newPortion);
    setEditingIndex(portions.length); // Set to the index of the new item
    setIsAddingNew(true);
  };

  const handleSavePortion = () => {
    if (!editingPortion || !editingPortion.portionName) return;

    // Check for duplicate portion names
    const isDuplicate = portions.some((p, idx) => {
      if (editingIndex !== null && idx === editingIndex) return false; // Skip self when editing
      return p.portionName.toLowerCase() === editingPortion.portionName.toLowerCase();
    });

    if (isDuplicate) {
      alert('A portion with this name already exists. Please use a different name.');
      return;
    }

    let newPortions: Portion[];
    
    if (isAddingNew) {
      newPortions = [...portions, editingPortion];
    } else if (editingIndex !== null) {
      newPortions = [...portions];
      newPortions[editingIndex] = editingPortion;
    } else {
      return;
    }

    // If the edited/new portion is set as default, unset all other defaults
    if (editingPortion.isDefault) {
      // Enforce zero modifier for default portion
      if (isAddingNew) {
        newPortions[newPortions.length - 1] = {
          ...newPortions[newPortions.length - 1],
          priceModifier: 0,
        };
      } else if (editingIndex !== null) {
        newPortions[editingIndex] = {
          ...newPortions[editingIndex],
          priceModifier: 0,
        };
      }
      newPortions = newPortions.map((p, idx) => ({
        ...p,
        isDefault: (isAddingNew && idx === newPortions.length - 1) || 
                  (!isAddingNew && idx === editingIndex)
      }));
    }

    setPortions(newPortions);
    setEditingPortion(null);
    setEditingIndex(null);
    setIsAddingNew(false);
  };

  const handleDeletePortion = (portion: Portion) => {
    const newPortions = portions.filter(p => p !== portion && p.id !== portion.id);
    
    // If we deleted the default, make the first one default
    if (portion.isDefault && newPortions.length > 0) {
      newPortions[0].isDefault = true;
    }
    
    setPortions(newPortions);
  };

  const handleSetDefault = (portion: Portion) => {
    // Check if this portion is currently the default
    if (portion.isDefault) {
      // Trying to REMOVE default status - show selection dialog
      const otherPortions = localPortions.filter(p => p !== portion && p.id !== portion.id);

      if (otherPortions.length === 0) {
        alert('Cannot remove default portion. You must have at least one portion. Please add another portion first.');
        return;
      }

      // Show dialog to select new default
      setSelectNewDefaultDialog({ open: true, currentDefaultPortion: portion });
    } else {
      // Trying to SET as default - recalculate modifiers to preserve actual prices
      applyNewDefault(portion);
    }
  };

  /**
   * Apply new default portion and recalculate modifiers to preserve actual prices
   */
  const applyNewDefault = (newDefaultPortion: Portion) => {
    const portionIndex = localPortions.findIndex(p => p === newDefaultPortion || p.id === newDefaultPortion.id);
    if (portionIndex === -1) return;

    // Step 1: Calculate actual current prices for all portions
    const actualPrices = localPortions.map(p => localItemPrice + (p.priceModifier || 0));

    // Step 2: The new default's current price becomes the new base price
    const newBase = actualPrices[portionIndex];

    // Step 3: Calculate new modifiers relative to the new base
    const updatedPortions = localPortions.map((p, idx) => ({
      ...p,
      isDefault: idx === portionIndex,
      priceModifier: actualPrices[idx] - newBase, // New modifier to preserve actual price
    }));

    // Update both portions and base price
    setLocalItemPrice(newBase);
    setPortions(updatedPortions);
  };
  
  /**
   * Handle selection of new default portion from the dialog
   */
  const handleSelectNewDefault = (newDefaultPortion: Portion) => {
    // Apply the new default with price recalculation
    applyNewDefault(newDefaultPortion);
    setSelectNewDefaultDialog({ open: false, currentDefaultPortion: null });
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      // Start from current portions and include in-progress edit if any
      let workingPortions: Portion[] = [...portions];
      if (editingPortion) {
        // Basic validation for unsaved edit
        if (!editingPortion.portionName) {
          alert('Please enter a portion name or cancel the edit before saving.');
          setLoading(false);
          return;
        }
        // Check for duplicate names (exclude self when editing)
        const isDup = workingPortions.some((p, idx) => {
          if (editingIndex !== null && !isAddingNew && idx === editingIndex) return false;
          return p.portionName.toLowerCase() === editingPortion.portionName.toLowerCase();
        });
        if (isDup) {
          alert('A portion with this name already exists. Please use a different name.');
          setLoading(false);
          return;
        }
        if (isAddingNew) {
          workingPortions = [...workingPortions, editingPortion];
        } else if (editingIndex !== null) {
          workingPortions = [...workingPortions];
          workingPortions[editingIndex] = editingPortion;
        }
      }

      // Filter out any invalid portions before saving
      let validPortions = workingPortions.filter(p => p && p.portionName);

      // ✅ CRITICAL VALIDATION: If portions exist, exactly one must be default
      if (validPortions.length > 0) {
        const defaultCount = validPortions.filter(p => p.isDefault).length;
        if (defaultCount === 0) {
          alert('Please select one portion as default before saving.');
          setLoading(false);
          return;
        }
        if (defaultCount > 1) {
          alert('Only one portion can be default. Please fix this before saving.');
          setLoading(false);
          return;
        }

        // ✅ CRITICAL VALIDATION: No negative prices
        const hasNegativePrices = validPortions.some(p => {
          const effectivePrice = localItemPrice + (p.priceModifier || 0);
          return effectivePrice < 0;
        });
        if (hasNegativePrices) {
          alert('Some portions have negative prices. Please adjust the base price or modifiers.');
          setLoading(false);
          return;
        }
      }
      
      // ✅ CRITICAL VALIDATION: Base price is valid
      const baseValidation = validateBasePrice(localItemPrice);
      if (!baseValidation.valid) {
        alert(baseValidation.error);
        setLoading(false);
        return;
      }
      
      // Normalize before save: exactly one default (first wins) and zero its modifier (only if portions exist)
      if (validPortions.length > 0) {
        const firstDefaultIndex = validPortions.findIndex(p => p.isDefault);
        if (firstDefaultIndex !== -1) {
          validPortions = validPortions.map((p, idx) => ({
            ...p,
            isDefault: idx === firstDefaultIndex,
            priceModifier: idx === firstDefaultIndex ? 0 : (p.priceModifier || 0),
          }));
        } else {
          // If no default found, make first one default
          validPortions = validPortions.map((p, idx) => ({
            ...p,
            isDefault: idx === 0,
            priceModifier: idx === 0 ? 0 : (p.priceModifier || 0),
          }));
        }
      }
      
      // If base price changed, update the menu item first
      if (localItemPrice !== menuItemPrice) {
        await api.patch(`/api/menu-items/${menuItemId}`, {
          price: localItemPrice,
        });
      }

      await api.put(`/api/menu-items/${menuItemId}/portions`, {
        portionTemplateId: selectedTemplate || null,
        portions: validPortions.map(({ id, ...rest }) => rest),
      });

      onPortionsUpdated();
      onClose();
    } catch (error) {
      console.error('Error saving portions:', error);
      alert('Failed to save portions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (modifier: number) => {
    if (modifier === 0) return '';
    return modifier > 0 ? `+${CURRENCY.symbol}${modifier.toFixed(2)}` : `-${CURRENCY.symbol}${Math.abs(modifier).toFixed(2)}`;
  };

  const calculateEffectivePrice = (modifier: number) => {
    return localItemPrice + modifier;
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const appTheme = useAppTheme();

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        Manage Portions - {menuItemName}
        <Typography variant="body2" color="textSecondary">
          Base Price: {CURRENCY.symbol}{localItemPrice.toFixed(2)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {initializing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
            <CircularProgress size={24} sx={{ mr: 1.5 }} />
            <Typography variant="body2" color="textSecondary">Loading portions…</Typography>
          </Box>
        )}
        {!initializing && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Use Template</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(e) => handleTemplateSelect(e.target.value as number)}
              label="Use Template"
            >
              <MuiMenuItem value="">
                <em>Custom</em>
              </MuiMenuItem>
              {templates.map((template) => {
                // Extract portion names from template options
                const portionNames = template.options
                  .sort((a, b) => a.order - b.order)
                  .map(opt => opt.name)
                  .join(', ');

                return (
                  <MuiMenuItem key={template.id} value={template.id}>
                    {portionNames}
                  </MuiMenuItem>
                );
              })}
            </Select>
          </FormControl>

          {isMobile ? (
            // Mobile Card Layout
            <Box sx={{ mb: 2 }}>
              {portions.filter(p => p && p.portionName).length === 0 && !isAddingNew ? (
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                      No portions defined. Add at least one portion.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Stack spacing={2}>
                  {portions.filter(p => p && p.portionName).map((portion, index) => (
                    <Card key={portion.id || index} variant="outlined">
                      <CardContent sx={{ pb: 2 }}>
                        {editingIndex === index ? (
                          // Editing Mode
                          <Box sx={{ space: 2 }}>
                            <TextField
                              label="Portion Name"
                              size="small"
                              value={editingPortion?.portionName || ''}
                              onChange={(e) => setEditingPortion({
                                ...editingPortion!,
                                portionName: e.target.value,
                              })}
                              placeholder="e.g., Full, Half"
                              fullWidth
                              sx={{ mb: 2 }}
                              error={!!(editingPortion?.portionName) && portions.some((p, idx) => 
                                idx !== editingIndex && 
                                p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                              )}
                              helperText={
                                editingPortion?.portionName && portions.some((p, idx) => 
                                  idx !== editingIndex && 
                                  p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                                ) ? 'This portion name already exists' : ''
                              }
                            />
                            
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                              Price Modifier
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setEditingPortion({
                                  ...editingPortion!,
                                  priceModifier: (editingPortion?.priceModifier || 0) - 1,
                                })}
                                disabled={editingPortion?.isDefault}
                                sx={{
                                  minWidth: 48,
                                  height: 40,
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                  backgroundColor: '#B8860B', /* basil-todo */
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: '#A0750A' /* basil-todo */
                                  }
                                }}
                              >
                                −
                              </Button>
                              
                              <TextField
                                size="small"
                                type="number"
                                value={editingPortion?.priceModifier === 0 ? '' : (editingPortion?.priceModifier ?? '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  // Allow empty string or just minus sign while typing
                                  if (val === '' || val === '-') {
                                    setEditingPortion({
                                      ...editingPortion!,
                                      priceModifier: 0,
                                    });
                                  } else {
                                    const parsed = parseFloat(val);
                                    if (!isNaN(parsed)) {
                                      setEditingPortion({
                                        ...editingPortion!,
                                        priceModifier: parsed,
                                      });
                                    }
                                  }
                                }}
                                onFocus={(e) => e.target.select()}
                                disabled={editingPortion?.isDefault}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">{CURRENCY.symbol}</InputAdornment>,
                                  inputProps: { 
                                    style: { fontSize: '16px', textAlign: 'center' },
                                    placeholder: '0'
                                  },
                                  sx: {
                                    '& input[type=number]': {
                                      MozAppearance: 'textfield',
                                      '&::-webkit-outer-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                      },
                                      '&::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                      },
                                    }
                                  }
                                }}
                                sx={{ flex: 1 }}
                              />
                              
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setEditingPortion({
                                  ...editingPortion!,
                                  priceModifier: (editingPortion?.priceModifier || 0) + 1,
                                })}
                                disabled={editingPortion?.isDefault}
                                sx={{
                                  minWidth: 48,
                                  height: 40,
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                  backgroundColor: '#B8860B', /* basil-todo */
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: '#A0750A' /* basil-todo */
                                  }
                                }}
                              >
                                +
                              </Button>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="body2">
                                Effective Price: {CURRENCY.symbol}{calculateEffectivePrice(editingPortion?.priceModifier || 0).toFixed(2)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mr: 1 }}>Default:</Typography>
                                <Switch
                                  checked={editingPortion?.isDefault || false}
                                  onChange={(e) => setEditingPortion({
                                    ...editingPortion!,
                                    isDefault: e.target.checked,
                                    priceModifier: e.target.checked ? 0 : (editingPortion?.priceModifier || 0),
                                  })}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#4A5E6D', /* basil-todo */
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#4A5E6D', /* basil-todo */
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <AppIconButton
                                size="small"
                                onClick={handleSavePortion}
                                data-testid="save-portion"
                                icon={<SaveIcon fontSize="small" />}
                                tooltip="Save"
                                variant="success"
                                sx={{
                                  width: 36,
                                  height: 36,
                                  p: 0.5,
                                  backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '22',
                                  color: appTheme.theme.button.success?.bg || appTheme.theme.success,
                                  '&:hover': {
                                    backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '44'
                                  }
                                }}
                              />
                              <AppIconButton
                                size="small"
                                onClick={() => {
                                  setEditingPortion(null);
                                  setEditingIndex(null);
                                  setIsAddingNew(false);
                                }}
                                data-testid="cancel-portion"
                                icon={<CancelIcon fontSize="small" />}
                                tooltip="Cancel"
                                variant="delete"
                                sx={{
                                  width: 36,
                                  height: 36,
                                  p: 0.5,
                                  backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '22',
                                  color: appTheme.theme.button.danger.solidBg || appTheme.theme.danger,
                                  '&:hover': {
                                    backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '44'
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          // Display Mode
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {portion?.portionName || ''}
                                </Typography>
                                {portion?.isDefault && (
                                  <Chip
                                    label="Default"
                                    size="small"
                                    color="primary"
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <AppIconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingPortion({...portion});
                                    setEditingIndex(index);
                                  }}
                                  data-testid={`edit-portion-${index}`}
                                  icon={<EditIcon fontSize="small" />}
                                  tooltip="Edit"
                                  variant="ghost"
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    p: 0.5,
                                    backgroundColor: 'transparent',
                                    color: appTheme.theme.gray?.[600] || appTheme.theme.button.secondary.text,
                                    '&:hover': {
                                      backgroundColor: appTheme.theme.gray?.[100] || appTheme.theme.button.secondary.bg
                                    }
                                  }}
                                />
                                <AppIconButton
                                  size="small"
                                  onClick={() => handleDeletePortion(portion)}
                                  data-testid={`delete-portion-${index}`}
                                  icon={<DeleteIcon fontSize="small" />}
                                  tooltip="Delete"
                                  variant="delete"
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    p: 0.5,
                                    backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '22',
                                    color: appTheme.theme.button.danger.solidBg || appTheme.theme.danger,
                                    '&:hover': {
                                      backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '44'
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                            
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textSecondary">
                                  Price Modifier
                                </Typography>
                                <Typography 
                                  variant="body2"
                                  color={(portion?.priceModifier || 0) > 0 ? 'success.main' : (portion?.priceModifier || 0) < 0 ? 'error.main' : 'textPrimary'}
                                  fontWeight="medium"
                                >
                                  {formatPrice(portion?.priceModifier || 0) || `${CURRENCY.symbol}0.00`}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textSecondary">
                                  Effective Price
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {CURRENCY.symbol}{calculateEffectivePrice(portion?.priceModifier || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mr: 1 }}>Default:</Typography>
                                <Switch
                                  checked={portion?.isDefault || false}
                                  onChange={() => handleSetDefault(portion)}
                                  size="small"
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#4A5E6D', /* basil-todo */
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#4A5E6D', /* basil-todo */
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Add new portion editing card when adding */}
                  {isAddingNew && editingIndex === portions.length && (
                    <Card variant="outlined" sx={{ border: '2px dashed', borderColor: 'primary.main' }}>
                      <CardContent sx={{ pb: 2 }}>
                        <Box sx={{ space: 2 }}>
                          <TextField
                            label="Portion Name"
                            size="small"
                            value={editingPortion?.portionName || ''}
                            onChange={(e) => setEditingPortion({
                              ...editingPortion!,
                              portionName: e.target.value,
                            })}
                            placeholder="e.g., Full, Half"
                            fullWidth
                            sx={{ mb: 2 }}
                            autoFocus
                            error={!!(editingPortion?.portionName) && portions.some((p, idx) => 
                              p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                            )}
                            helperText={
                              editingPortion?.portionName && portions.some((p, idx) => 
                                p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                              ) ? 'This portion name already exists' : ''
                            }
                          />
                          
                          <TextField
                            label="Price Modifier"
                            size="small"
                            type="number"
                            value={editingPortion?.priceModifier || 0}
                            onChange={(e) => setEditingPortion({
                              ...editingPortion!,
                              priceModifier: parseFloat(e.target.value) || 0,
                            })}
                            disabled={editingPortion?.isDefault}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{CURRENCY.symbol}</InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    width: 16,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 0.5,
                                    overflow: 'hidden'
                                  }}>
                                    <Box
                                      onClick={() => setEditingPortion({
                                        ...editingPortion!,
                                        priceModifier: (editingPortion?.priceModifier || 0) + 1,
                                      })}
                                      sx={{ 
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: editingPortion?.isDefault ? 'not-allowed' : 'pointer',
                                        pointerEvents: editingPortion?.isDefault ? 'none' : 'auto',
                                        fontSize: '8px',
                                        backgroundColor: 'background.paper',
                                        borderBottom: '0.5px solid',
                                        borderBottomColor: 'divider',
                                        '&:hover': {
                                          backgroundColor: 'action.hover'
                                        },
                                        '&:active': {
                                          backgroundColor: 'action.selected'
                                        }
                                      }}
                                    >
                                      ▲
                                    </Box>
                                    <Box
                                      onClick={() => setEditingPortion({
                                        ...editingPortion!,
                                        priceModifier: (editingPortion?.priceModifier || 0) - 1,
                                      })}
                                      sx={{ 
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: editingPortion?.isDefault ? 'not-allowed' : 'pointer',
                                        pointerEvents: editingPortion?.isDefault ? 'none' : 'auto',
                                        fontSize: '8px',
                                        backgroundColor: 'background.paper',
                                        '&:hover': {
                                          backgroundColor: 'action.hover'
                                        },
                                        '&:active': {
                                          backgroundColor: 'action.selected'
                                        }
                                      }}
                                    >
                                      ▼
                                    </Box>
                                  </Box>
                                </InputAdornment>
                              ),
                              inputProps: { 
                                style: { fontSize: '16px' }
                              },
                              sx: {
                                '& input[type=number]': {
                                  MozAppearance: 'textfield',
                                  '&::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '&::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                }
                              }
                            }}
                            fullWidth
                            sx={{ mb: 2 }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2">
                              Effective Price: {CURRENCY.symbol}{calculateEffectivePrice(editingPortion?.priceModifier || 0).toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ mr: 1 }}>Default:</Typography>
                              <Switch
                                checked={editingPortion?.isDefault || false}
                                onChange={(e) => setEditingPortion({
                                  ...editingPortion!,
                                  isDefault: e.target.checked,
                                  priceModifier: e.target.checked ? 0 : (editingPortion?.priceModifier || 0),
                                })}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#4A5E6D', /* basil-todo */
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#4A5E6D', /* basil-todo */
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <AppIconButton
                              size="small"
                              onClick={handleSavePortion}
                              data-testid="save-portion-card"
                              icon={<SaveIcon fontSize="small" />}
                              tooltip="Save"
                              variant="success"
                              sx={{
                                width: 36,
                                height: 36,
                                p: 0.5,
                                backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '22',
                                color: appTheme.theme.button.success?.bg || appTheme.theme.success,
                                '&:hover': {
                                  backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '44'
                                }
                              }}
                            />
                            <AppIconButton
                              size="small"
                              onClick={() => {
                                setEditingPortion(null);
                                setEditingIndex(null);
                                setIsAddingNew(false);
                              }}
                              data-testid="cancel-portion-card"
                              icon={<CancelIcon fontSize="small" />}
                              tooltip="Cancel"
                              variant="delete"
                              sx={{
                                width: 36,
                                height: 36,
                                p: 0.5,
                                backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '22',
                                color: appTheme.theme.button.danger.solidBg || appTheme.theme.danger,
                                '&:hover': {
                                  backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '44'
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              )}
            </Box>
          ) : (
            // Desktop Table Layout
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Portion Name</TableCell>
                    <TableCell align="right">Price Modifier</TableCell>
                    <TableCell align="right">Effective Price</TableCell>
                    <TableCell align="center">Default</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portions.filter(p => p && p.portionName).map((portion, index) => (
                    <TableRow key={portion.id || index}>
                      {editingIndex === index ? (
                        <>
                          <TableCell>
                            <TextField
                              size="small"
                              value={editingPortion?.portionName || ''}
                              onChange={(e) => setEditingPortion({
                                ...editingPortion!,
                                portionName: e.target.value,
                              })}
                              placeholder="e.g., Full, Half"
                              fullWidth
                              error={!!(editingPortion?.portionName) && portions.some((p, idx) => 
                                idx !== editingIndex && 
                                p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                              )}
                              helperText={
                                editingPortion?.portionName && portions.some((p, idx) => 
                                  idx !== editingIndex && 
                                  p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                                ) ? 'This portion name already exists' : ''
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={editingPortion?.priceModifier === 0 ? '' : (editingPortion?.priceModifier ?? '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                // Allow empty string or just minus sign while typing
                                if (val === '' || val === '-') {
                                  setEditingPortion({
                                    ...editingPortion!,
                                    priceModifier: 0,
                                  });
                                } else {
                                  const parsed = parseFloat(val);
                                  if (!isNaN(parsed)) {
                                    setEditingPortion({
                                      ...editingPortion!,
                                      priceModifier: parsed,
                                    });
                                  }
                                }
                              }}
                              onFocus={(e) => e.target.select()}
                              disabled={editingPortion?.isDefault}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{CURRENCY.symbol}</InputAdornment>,
                                inputProps: { 
                                  placeholder: '0'
                                },
                              }}
                              sx={{ width: 120 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {CURRENCY.symbol}{calculateEffectivePrice(editingPortion?.priceModifier || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={editingPortion?.isDefault || false}
                              onChange={(e) => setEditingPortion({
                                ...editingPortion!,
                                isDefault: e.target.checked,
                                priceModifier: e.target.checked ? 0 : (editingPortion?.priceModifier || 0),
                              })}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4A5E6D', /* basil-todo */
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#4A5E6D', /* basil-todo */
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <AppIconButton
                              size="small"
                              onClick={handleSavePortion}
                              data-testid="save-portion-table"
                              icon={<SaveIcon />}
                              tooltip="Save"
                              variant="success"
                              sx={{
                                mr: 1,
                                width: 36,
                                height: 36,
                                p: 0.5,
                                backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '22',
                                color: appTheme.theme.button.success?.bg || appTheme.theme.success,
                                '&:hover': {
                                  backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '44'
                                }
                              }}
                            />
                            <AppIconButton
                              size="small"
                              onClick={() => {
                                setEditingPortion(null);
                                setEditingIndex(null);
                                setIsAddingNew(false);
                              }}
                              data-testid="cancel-portion-table"
                              icon={<CancelIcon />}
                              tooltip="Cancel"
                              variant="delete"
                              sx={{
                                width: 36,
                                height: 36,
                                p: 0.5,
                                backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '22',
                                color: appTheme.theme.button.danger.solidBg || appTheme.theme.danger,
                                '&:hover': {
                                  backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '44'
                                }
                              }}
                            />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {portion?.portionName || ''}
                            {portion?.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              color={(portion?.priceModifier || 0) > 0 ? 'success.main' : (portion?.priceModifier || 0) < 0 ? 'error.main' : 'textPrimary'}
                            >
                              {formatPrice(portion?.priceModifier || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {CURRENCY.symbol}{calculateEffectivePrice(portion?.priceModifier || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={portion?.isDefault || false}
                              onChange={() => handleSetDefault(portion)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4A5E6D', /* basil-todo */
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#4A5E6D', /* basil-todo */
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <AppIconButton
                              size="small"
                              onClick={() => {
                                setEditingPortion({...portion});
                                setEditingIndex(index);
                              }}
                              data-testid={`edit-portion-table-${index}`}
                              icon={<EditIcon fontSize="small" />}
                              tooltip="Edit"
                              variant="subtle"
                              sx={{
                                mr: 1,
                                width: 36,
                                height: 36,
                                p: 0.5,
                                backgroundColor: appTheme.theme.primary + '22',
                                color: appTheme.theme.primary,
                                '&:hover': {
                                  backgroundColor: appTheme.theme.primary + '44'
                                }
                              }}
                            />
                            <AppIconButton
                              size="small"
                              onClick={() => handleDeletePortion(portion)}
                              data-testid={`delete-portion-table-${index}`}
                              icon={<DeleteIcon fontSize="small" />}
                              tooltip="Delete"
                              variant="delete"
                              sx={{
                                width: 36,
                                height: 36,
                                p: 0.5,
                                backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '22',
                                color: appTheme.theme.button.danger.solidBg || appTheme.theme.danger,
                                '&:hover': {
                                  backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '44'
                                }
                              }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {isAddingNew && editingIndex === portions.length && (
                    <TableRow>
                      <>
                        <TableCell>
                          <TextField
                            size="small"
                            value={editingPortion?.portionName || ''}
                            onChange={(e) => setEditingPortion({
                              ...editingPortion!,
                              portionName: e.target.value,
                            })}
                            placeholder="e.g., Full, Half"
                            fullWidth
                            error={!!(editingPortion?.portionName) && portions.some((p) => 
                              p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                            )}
                            helperText={
                              editingPortion?.portionName && portions.some((p) => 
                                p.portionName.toLowerCase() === (editingPortion?.portionName || '').toLowerCase()
                              ) ? 'This portion name already exists' : ''
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={editingPortion?.priceModifier === 0 ? '' : (editingPortion?.priceModifier ?? '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              // Allow empty string or just minus sign while typing
                              if (val === '' || val === '-') {
                                setEditingPortion({
                                  ...editingPortion!,
                                  priceModifier: 0,
                                });
                              } else {
                                const parsed = parseFloat(val);
                                if (!isNaN(parsed)) {
                                  setEditingPortion({
                                    ...editingPortion!,
                                    priceModifier: parsed,
                                  });
                                }
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                            disabled={editingPortion?.isDefault}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{CURRENCY.symbol}</InputAdornment>,
                              inputProps: { 
                                placeholder: '0'
                              },
                            }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {CURRENCY.symbol}{calculateEffectivePrice(editingPortion?.priceModifier || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={editingPortion?.isDefault || false}
                            onChange={(e) => setEditingPortion({
                              ...editingPortion!,
                              isDefault: e.target.checked,
                              priceModifier: e.target.checked ? 0 : (editingPortion?.priceModifier || 0),
                            })}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#4A5E6D', /* basil-todo */
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#4A5E6D', /* basil-todo */
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <AppIconButton
                            size="small"
                            onClick={handleSavePortion}
                            data-testid="save-portion-add-row"
                            icon={<SaveIcon />}
                            tooltip="Save"
                            variant="success"
                            sx={{
                              mr: 1,
                              width: 36,
                              height: 36,
                              p: 0.5,
                              backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '22',
                              color: appTheme.theme.button.success?.bg || appTheme.theme.success,
                              '&:hover': {
                                backgroundColor: (appTheme.theme.button.success?.bg || appTheme.theme.success) + '44'
                              }
                            }}
                          />
                          <AppIconButton
                            size="small"
                            onClick={() => {
                              setEditingPortion(null);
                              setEditingIndex(null);
                              setIsAddingNew(false);
                            }}
                            data-testid="cancel-portion-add-row"
                            icon={<CancelIcon />}
                            tooltip="Cancel"
                            variant="delete"
                            sx={{
                              width: 36,
                              height: 36,
                              p: 0.5,
                              backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '22',
                              color: appTheme.theme.button.danger.solidBg || appTheme.theme.danger,
                              '&:hover': {
                                backgroundColor: (appTheme.theme.button.danger.solidBg || appTheme.theme.danger) + '44'
                              }
                            }}
                          />
                        </TableCell>
                      </>
                    </TableRow>
                  )}
                  {portions.filter(p => p && p.portionName).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">
                          No portions defined. Add at least one portion.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddPortion}
              disabled={!!editingPortion}
              variant={isMobile ? "contained" : "text"}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                backgroundColor: 'transparent !important',
                color: appTheme.theme.primary + ' !important',
                border: `1px solid ${appTheme.theme.primary} !important`,
                fontWeight: 500,
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: (appTheme.theme.gray?.[100] || '#f3f4f6') + ' !important',
                  borderColor: appTheme.theme.primary + ' !important',
                  boxShadow: 'none !important'
                },
                '&:disabled': {
                  backgroundColor: 'var(--bg-sunken) !important',
                  color: 'var(--text-tertiary) !important',
                  border: '1px solid var(--border-subtle) !important'
                }
              }}
            >
              Add Portion
            </Button>
          </Box>
        </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 },
        p: { xs: 2, sm: 1 }
      }}>
        <Button
          onClick={onClose}
          fullWidth={isMobile}
          sx={{ 
            order: { xs: 2, sm: 1 },
            whiteSpace: 'nowrap',
            minWidth: { xs: 'auto', sm: 'auto' },
            backgroundColor: 'transparent !important',
            color: (appTheme.theme.gray?.[700] || '#374151') + ' !important',
            border: `1px solid ${appTheme.theme.gray?.[300] || '#d1d5db'} !important`,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: (appTheme.theme.gray?.[100] || '#f3f4f6') + ' !important',
              borderColor: (appTheme.theme.gray?.[400] || '#9ca3af') + ' !important'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveAll}
          variant="contained"
          disabled={initializing || loading}
          fullWidth={isMobile}
          sx={{
            order: { xs: 1, sm: 2 },
            whiteSpace: 'nowrap',
            minWidth: { xs: 'auto', sm: 'auto' },
            backgroundColor: appTheme.theme.primary + ' !important',
            color: 'var(--text-inverse) !important',
            border: 'none !important',
            fontWeight: 600,
            boxShadow: 'none !important',
            '&:hover': {
              backgroundColor: (appTheme.theme.primaryHover || appTheme.theme.primary) + ' !important',
              filter: 'brightness(1.1)',
              boxShadow: 'none !important'
            },
            '&:disabled': {
              backgroundColor: (appTheme.theme.gray?.[200] || '#e5e7eb') + ' !important',
              color: (appTheme.theme.gray?.[500] || '#9ca3af') + ' !important',
              border: '1px solid var(--border-subtle) !important'
            }
          }}
        >
          Save Portions
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* ========================================================================== */}
    {/* Simple Select New Default Dialog */}
    {/* ========================================================================== */}
    <Dialog
      open={selectNewDefaultDialog.open}
      onClose={() => setSelectNewDefaultDialog({ open: false, currentDefaultPortion: null })}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Select New Default Portion
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You must select another portion to become the default before removing default status from{' '}
          <strong>{selectNewDefaultDialog.currentDefaultPortion?.portionName}</strong>.
        </Alert>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Select which portion should be the new default:
        </Typography>
        <Stack spacing={1}>
          {localPortions
            .filter(p =>
              p !== selectNewDefaultDialog.currentDefaultPortion &&
              p.id !== selectNewDefaultDialog.currentDefaultPortion?.id
            )
            .map((portion) => (
              <Paper
                key={portion.id || portion.portionName}
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                }}
                onClick={() => handleSelectNewDefault(portion)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {portion.portionName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {CURRENCY.symbol}{calculateEffectivePrice(portion.priceModifier || 0).toFixed(2)}
                  </Typography>
                </Box>
                {portion.priceModifier !== 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Modifier: {formatPrice(portion.priceModifier || 0)}
                  </Typography>
                )}
              </Paper>
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setSelectNewDefaultDialog({ open: false, currentDefaultPortion: null })}
          fullWidth={isMobile}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};


export default MenuItemPortionsManager;